import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Save, Trash2, Loader2, MoreHorizontal } from "lucide-react";
import { DetailDensityDropdown } from "@/features/common/components/DetailDensityDropdown";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { useSmartDeleteFlow } from "@/features/common/api/useSmartDeleteFlow";
import {
  SmartArchiveDialog,
  ChoiceDialog,
  PermanentDeleteConfirm,
} from "@/features/common/components/archive-delete";
import { buildListRoute } from "@/lib/config/docRoutes";

interface DocHeaderActionsProps {
  doctype: string;
  /** Document name — required for the smart delete safety check */
  docName?: string;
  onSave: () => void;
  /** Called for permanent deletion. The caller's hook handles toast + navigation. */
  onPermanentDelete?: () => void;
  isPermanentDeleting?: boolean;
  isSaving: boolean;
  isDirty: boolean;
  isNew?: boolean;
}

/**
 * Generic header actions for DocType detail pages.
 *
 * Provides Save + smart Delete flow (safety check → archive or permanent delete).
 * Works for any doctype (Sales Order, Customer, etc.)
 */
export function DocHeaderActions({
  doctype,
  docName,
  onSave,
  onPermanentDelete,
  isPermanentDeleting = false,
  isSaving,
  isDirty,
  isNew = false,
}: DocHeaderActionsProps) {
  const navigate = useNavigate();
  const {
    detailDensity,
    setDetailDensity,
    detailTextSize,
    setDetailTextSize,
  } = usePreferencesStore();
  const [showActionsDialog, setShowActionsDialog] = useState(false);

  const smartDelete = useSmartDeleteFlow(doctype, docName ?? "");

  const showOverflow = !isNew && !!onPermanentDelete;

  return (
    <div className="flex items-center gap-2">
      {/* Density */}
      <DetailDensityDropdown
        density={detailDensity}
        setDensity={setDetailDensity}
        textSize={detailTextSize}
        setTextSize={setDetailTextSize}
      />

      {showOverflow && (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowActionsDialog(true)}
            className="h-10 w-10 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 touch-manipulation"
          >
            <MoreHorizontal className="h-5 w-5" />
          </Button>

          {/* Actions Modal */}
          <Dialog
            open={showActionsDialog}
            onOpenChange={setShowActionsDialog}
          >
            <DialogContent className="max-w-md p-0 flex flex-col">
              <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
                <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px]">
                  Actions
                </DialogTitle>
              </DialogHeader>
              <div className="px-10 pb-8 pt-2 space-y-1">
                {/* Delete — smart flow */}
                <button
                  onClick={() => {
                    setShowActionsDialog(false);
                    smartDelete.initiateDelete();
                  }}
                  disabled={smartDelete.isChecking}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left group"
                >
                  <Trash2 className="size-5 text-destructive group-hover:scale-110 transition-transform" />
                  <div className="flex-1">
                    <div className="font-medium text-destructive">Delete</div>
                    <div className="text-sm text-muted-foreground">
                      Remove this {doctype.toLowerCase()}
                    </div>
                  </div>
                </button>
              </div>
              <DialogFooter className="px-10 pb-8">
                <Button
                  variant="outline"
                  onClick={() => setShowActionsDialog(false)}
                >
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Smart Delete Dialogs */}
          <SmartArchiveDialog
            open={smartDelete.dialogState === "smart-archive"}
            onOpenChange={(open) => {
              if (!open) smartDelete.closeDialogs();
            }}
            itemName={docName ?? ""}
            linkedSummary={smartDelete.safetyData?.linked_summary ?? ""}
            onArchive={() => {
              smartDelete.archiveMutation.mutate(
                { name: docName ?? "" },
                { onSuccess: () => navigate(buildListRoute(doctype)) },
              );
            }}
            isArchiving={smartDelete.archiveMutation.isPending}
          />
          <ChoiceDialog
            open={smartDelete.dialogState === "choice"}
            onOpenChange={(open) => {
              if (!open) smartDelete.closeDialogs();
            }}
            itemName={docName ?? ""}
            hasDisabledField={
              smartDelete.safetyData?.has_disabled_field ?? true
            }
            onArchive={() => {
              smartDelete.archiveMutation.mutate(
                { name: docName ?? "" },
                { onSuccess: () => navigate(buildListRoute(doctype)) },
              );
            }}
            onPermanentDelete={() =>
              smartDelete.setDialogState("permanent-delete")
            }
            isArchiving={smartDelete.archiveMutation.isPending}
          />
          <PermanentDeleteConfirm
            open={smartDelete.dialogState === "permanent-delete"}
            onOpenChange={(open) => {
              if (!open) smartDelete.closeDialogs();
            }}
            itemName={docName ?? ""}
            doctype={doctype}
            onConfirm={() => {
              onPermanentDelete?.();
              // Caller's delete hook handles toast + navigation
            }}
            isDeleting={isPermanentDeleting}
          />
        </>
      )}

      <Button
        variant="default"
        size="default"
        onClick={onSave}
        disabled={!isDirty || isSaving}
        className="h-10 px-4 text-sm font-normal rounded-full hover:scale-105 active:scale-95 transition-all duration-300 ease-out shrink-0 bg-[#007AFF] hover:bg-[#0051D5] text-white disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? (
          <>
            <Loader2 className="size-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="size-4 mr-2" />
            Save
          </>
        )}
      </Button>
    </div>
  );
}
